import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";

interface ProtectedRouteProps {
  children: ReactNode;
}

// Routes that should always be accessible (for upgrading/billing)
const ALLOWED_ROUTES_WHEN_EXPIRED = [
  "/settings",
  "/dashboard/settings",
  "/trial-expired",
];

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { isTrialExpired, hasActiveSubscription, isLoading: trialLoading, sessionInvalid } = useTrialStatus();
  const location = useLocation();

  if (loading || trialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If session is invalid (stale JWT from deleted user), redirect to auth
  if (sessionInvalid) {
    return <Navigate to="/auth" replace />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if current route is allowed when trial is expired
  const isAllowedRoute = ALLOWED_ROUTES_WHEN_EXPIRED.some(route => 
    location.pathname === route || location.pathname.startsWith(route + "/")
  );

  // If trial expired, no active subscription, and not on an allowed route
  if (isTrialExpired && !hasActiveSubscription && !isAllowedRoute) {
    return <Navigate to="/trial-expired" replace />;
  }

  return <>{children}</>;
};
